import React, { useEffect, useRef, useState } from "react";
import { FilterList, Schedule } from "@mui/icons-material";
import {
  Button,
  Grid,
  styled,
  Snackbar,
  Autocomplete,
  Checkbox,
  TextField,
  Box,
  Card,
  InputLabel,
  Collapse,
  Typography,
  CircularProgress,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Footer from "../../../Components/Footer";
import ReportTable from "../components/ReportTable";
import VisualizationGraph from "../components/VisualizationGraph";
import useReportData from "../hooks/useReportData";
import { useFinancialYears } from "../hooks/useFinancialYears";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import httpclient from "../../../Utils";
import useScheduleState from "../hooks/useScheduleState";
import ScheduleReport from "../components/ScheduleReport";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const initialFilters = {
  sites: [],
  states: [],
  companies: [],
  financialYear: null,
  zontMileRange: [null, null],
  comparisonRange: [null, null],
};

// Helper functions for date handling
const formatDateToYYYYMMDD = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const convertToIncludeDates = (dateData) => {
  if (!Array.isArray(dateData)) {
    console.error("Invalid input: Expected an array");
    return [];
  }
  return dateData
    .map((item) => {
      if (!item || !item.date) {
        console.warn("Invalid item: Missing date property", item);
        return null;
      }
      const [year, month, day] = item.date.split("-").map(Number);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
      console.warn("Invalid date format:", item.date);
      return null;
    })
    .filter((dateObj) => dateObj !== null);
};

const buildQueryString = (filterValues) => {
  let queryParts = [];
  // Add sites parameters
  if (filterValues.sites?.length > 0) {
    filterValues.sites.forEach((site, index) => {
      queryParts.push(`stores[${index}]=${site.id}`);
    });
  }
  // Add states parameters
  if (filterValues.states?.length > 0) {
    filterValues.states.forEach((state, index) => {
      queryParts.push(`states[${index}]=${state.state}`);
    });
  }
  // Add companies parameters
  if (filterValues.companies?.length > 0) {
    filterValues.companies.forEach((company, index) => {
      queryParts.push(`company_id[${index}]=${company.id}`);
    });
  }
  // Add financial year
  if (filterValues.financialYear) {
    queryParts.push(`financial_year=${filterValues.financialYear.id}`);
  }
  // Add Zont Mile Sales Day range
  if (filterValues.zontMileRange && Array.isArray(filterValues.zontMileRange)) {
    if (filterValues.zontMileRange[0]) {
      queryParts.push(
        `zont_start_date=${formatDateToYYYYMMDD(filterValues.zontMileRange[0])}`
      );
    }
    if (filterValues.zontMileRange[1]) {
      queryParts.push(
        `zont_end_date=${formatDateToYYYYMMDD(filterValues.zontMileRange[1])}`
      );
    }
  }
  // Add Comparison Date range
  if (
    filterValues.comparisonRange &&
    Array.isArray(filterValues.comparisonRange)
  ) {
    if (filterValues.comparisonRange[0]) {
      queryParts.push(
        `comparison_start_date=${formatDateToYYYYMMDD(
          filterValues.comparisonRange[0]
        )}`
      );
    }
    if (filterValues.comparisonRange[1]) {
      queryParts.push(
        `comparison_end_date=${formatDateToYYYYMMDD(
          filterValues.comparisonRange[1]
        )}`
      );
    }
  }
  return queryParts.length > 0 ? `&${queryParts.join("&")}` : "";
};

const Header = styled("div")(({ theme }) => ({
  "& h1": {
    color: theme.palette.primary.dark,
    margin: "0",
    fontWeight: 600,
    fontSize: "1.75rem",
  },
}));

const FilterCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  overflow: "visible",
}));

const HighlightedDate = styled("div")(({ theme }) => ({
  position: "relative",
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: "4px",
  width: "32px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.primary.main + "15",
  color: theme.palette.primary.dark,
  fontWeight: "bold",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  "&:hover": {
    backgroundColor: theme.palette.primary.main + "25",
    transform: "scale(1.05)",
    transition: "all 0.2s ease",
  },
}));

// Custom Range Picker Component with selectsRange
const RangePicker = ({ label, value, onChange, disabled, includeDates }) => {
  // Custom day renderer for DatePicker
  const renderDayContents = (day, date) => {
    const isHighlighted = includeDates.some(
      (includeDate) =>
        date.getDate() === includeDate.getDate() &&
        date.getMonth() === includeDate.getMonth() &&
        date.getFullYear() === includeDate.getFullYear()
    );

    return isHighlighted ? (
      <HighlightedDate>{day}</HighlightedDate>
    ) : (
      <div>{day}</div>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <InputLabel sx={{ fontWeight: 500, mb: 1 }}>{label}</InputLabel>

      <Box
        sx={{
          "& .react-datepicker-wrapper": {
            width: "100%",
          },
          "& .react-datepicker__input-container": {
            width: "100%",
          },
          "& input": {
            width: "100%",
            padding: "10px 14px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "0.95rem",
          },
        }}
      >
        <DatePicker
          selected={value[0]}
          onChange={onChange}
          startDate={value[0]}
          endDate={value[1]}
          selectsRange
          dateFormat="MMMM d, yyyy"
          placeholderText="Select date range"
          disabled={disabled}
          renderDayContents={renderDayContents}
          calendarClassName="custom-calendar"
          wrapperClassName="date-picker-wrapper"
        />
      </Box>
    </Box>
  );
};

const CustomReportFilter = ({
  open,
  filters,
  states,
  sites,
  companies,
  financialYearOptions,
  onFilterChange,
  onApply,
  onReset,
  includeDates,
  loading,
}) => {
  // Ensure ranges are arrays
  const zontRange = Array.isArray(filters.zontMileRange)
    ? filters.zontMileRange
    : [null, null];
  const comparisonRange = Array.isArray(filters.comparisonRange)
    ? filters.comparisonRange
    : [null, null];

  return (
    <Grid item xs={12}>
      <Collapse in={open}>
        <FilterCard>
          <Box p={3}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* First Row: States, Sites and Companies */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <InputLabel>States</InputLabel>
                    <Autocomplete
                      options={states}
                      onChange={(event, newValue) =>
                        onFilterChange("states", newValue)
                      }
                      multiple
                      id="states-filter"
                      disableCloseOnSelect
                      value={filters.states}
                      getOptionLabel={(option) => option.state}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.state}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select states"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Sites</InputLabel>
                    <Autocomplete
                      options={sites}
                      onChange={(event, newValue) =>
                        onFilterChange("sites", newValue)
                      }
                      multiple
                      id="sites-filter"
                      disableCloseOnSelect
                      value={filters.sites}
                      getOptionLabel={(option) => option.manualStoreCode}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.manualStoreCode}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select sites"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputLabel>Companies</InputLabel>
                    <Autocomplete
                      options={companies}
                      onChange={(event, newValue) =>
                        onFilterChange("companies", newValue)
                      }
                      multiple
                      id="companies-filter"
                      disableCloseOnSelect
                      value={filters.companies}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select companies"
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Second Row: Date Ranges */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <RangePicker
                      label="Zont Mile Sales Day Range"
                      value={zontRange}
                      onChange={(newRange) =>
                        onFilterChange("zontMileRange", newRange)
                      }
                      disabled={filters.financialYear !== null}
                      includeDates={includeDates}
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      <Box
                        component="span"
                        sx={{ display: "inline-flex", alignItems: "center", mr: 1 }}
                      >
                        <Box
                          sx={{
                            width: "24px",
                            height: "24px",
                            border: "2px solid",
                            borderColor: "primary.main",
                            borderRadius: "4px",
                            backgroundColor: (theme) =>
                              theme.palette.primary.main + "15",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: "bold",
                              color: (theme) => theme.palette.primary.dark,
                            }}
                          ></Typography>
                        </Box>
                        Zont Mile Days
                      </Box>
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <RangePicker
                      label="Comparison Date Range"
                      value={comparisonRange}
                      onChange={(newRange) =>
                        onFilterChange("comparisonRange", newRange)
                      }
                      disabled={filters.financialYear !== null}
                      includeDates={[]}
                    />
                  </Grid>
                </Grid>

                {/* Action Buttons */}
                <Grid container>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={onReset}
                        sx={{ px: 3 }}
                      >
                        Reset Filters
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onApply}
                        endIcon={<ArrowForwardIcon fontSize="small" />}
                        sx={{ px: 3 }}
                      >
                        Apply Filters
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        </FilterCard>
      </Collapse>
    </Grid>
  );
};

// Custom ActiveFilters Component
const FilteredBox = styled(Box)(({ theme }) => ({
  background: "#f9f9f9",
  padding: "5px 10px",
  borderRadius: "5px",
  "& p": {
    margin: "3px 0",
    marginRight: "10px",
    display: "inline-block",
    background: "#dedede",
    borderRadius: "10px",
    padding: "2px 5px",
  },
  "& svg": {
    fontSize: "15px",
    cursor: "pointer",
    position: "relative",
    top: "3px",
    background: theme.palette.primary.dark,
    color: "#fff",
    borderRadius: "50%",
    padding: "2px",
    marginLeft: "2px",
  },
}));

const ActiveFiltersComponent = ({ filters, onRemoveFilter, showTime = false }) => {
  // Format date to display with optional time
  const formatDateTime = (date) => {
    if (!date) return "";
    
    const dateObj = new Date(date);
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) return "";
    
    if (showTime) {
      // Format with time
      return dateObj.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      // Format without time
      return dateObj.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // Format date range for display
  const formatDateRange = (range) => {
    if (!range || (!range[0] && !range[1])) return "";
    
    const startDisplay = range[0] ? formatDateTime(range[0]) : "";
    const endDisplay = range[1] ? formatDateTime(range[1]) : "";
    
    if (startDisplay && endDisplay) {
      return `${startDisplay} to ${endDisplay}`;
    } else if (startDisplay) {
      return `From ${startDisplay}`;
    } else if (endDisplay) {
      return `To ${endDisplay}`;
    }
    
    return "";
  };

  return (
    <FilteredBox m={2} p={2}>
      <span>Active Filters: </span>
      
      {/* Sites filter */}
      {filters.sites && filters.sites.length > 0 && (
        <p>
          <span>Sites: {filters.sites.map(site => site.manualStoreCode || site.name || site).join(", ")}</span>
          <CloseIcon
            fontSize="small"
            onClick={() => onRemoveFilter("sites")}
          />
        </p>
      )}
      
      {/* States filter */}
      {filters.states && filters.states.length > 0 && (
        <p>
          <span>States: {filters.states.map(state => state.state || state.name || state).join(", ")}</span>
          <CloseIcon
            fontSize="small"
            onClick={() => onRemoveFilter("states")}
          />
        </p>
      )}
      
      {/* Companies filter */}
      {filters.companies && filters.companies.length > 0 && (
        <p>
          <span>Companies: {filters.companies.map(company => company.name || company).join(", ")}</span>
          <CloseIcon
            fontSize="small"
            onClick={() => onRemoveFilter("companies")}
          />
        </p>
      )}
      
      {/* Financial Year filter */}
      {filters.financialYear && (
        <p>
          <span>Financial Year: {filters.financialYear.name || filters.financialYear}</span>
          <CloseIcon
            fontSize="small"
            onClick={() => onRemoveFilter("financialYear")}
          />
        </p>
      )}
      
      {/* Zont Mile Sales Day Range filter */}
      {filters.zontMileRange && (filters.zontMileRange[0] || filters.zontMileRange[1]) && (
        <p>
          <span>
            Zont Mile Sales Day Range: {formatDateRange(filters.zontMileRange)}
          </span>
          <CloseIcon
            fontSize="small"
            onClick={() => onRemoveFilter("zontMileRange")}
          />
        </p>
      )}
      
      {/* Comparison Date Range filter */}
      {filters.comparisonRange && (filters.comparisonRange[0] || filters.comparisonRange[1]) && (
        <p>
          <span>
            Comparison Date Range: {formatDateRange(filters.comparisonRange)}
          </span>
          <CloseIcon
            fontSize="small"
            onClick={() => onRemoveFilter("comparisonRange")}
          />
        </p>
      )}
    </FilteredBox>
  );
};

const DoubleZontMileSales = (props) => {
  const {
    getTokenRefreshed: refresh,
    open: tokenOpen,
    overlay,
    setOpen: setTokenOpen,
    message: tokenMessage,
    messageState: tokenMessageState,
  } = useTokenRefresh();
  
  const [filterOpen, setFilterOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageState, setMessageState] = useState("");
  // Filter state
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [hasApplied, setHasApplied] = useState(false);
  // Date restrictions
  const [includeDates, setIncludeDates] = useState([]);

  // State for sites and states data
  const [statesData, setStatesData] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({});
  const [scheduleId, setScheduleId] = useState(null);
  
  // Loading states
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const [initialDataError, setInitialDataError] = useState(null);

  const { schedule, updateSchedule } = useScheduleState(scheduleData);

  // Sequential data fetching with specified delays
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setInitialDataLoading(true);
        setInitialDataError(null);

        // Wait 500ms
        await new Promise(resolve => setTimeout(resolve, 500));

        // Call other APIs in parallel without delays between them
        const [zontMileResponse, storesResponse, companiesResponse] = await Promise.all([
          httpclient.get("/request-response?requestName=lightspeed/advanced-report/double-zont-mile-days"),
          httpclient.get("/request-response?requestName=lightspeed/advanced-report/stores"),
          httpclient.get("/request-response?requestName=lightspeed/advanced-report/companies")
        ]);

        // Process responses
        if (zontMileResponse.status === 200) {
          const dates = convertToIncludeDates(zontMileResponse.data);
          setIncludeDates(dates);
        } else {
          throw new Error("Failed to fetch Zont Mile Days");
        }

        setStatesData(storesResponse.data);
        setCompaniesData(companiesResponse.data);

        // Fetch schedule data
        await getScheduleData();

      } catch (error) {
        console.error("Error fetching initial data:", error);
        setInitialDataError(error);
        setOpen(true);
        setMessage("Failed to load initial data");
        setMessageState("error");
      } finally {
        setInitialDataLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const financialYearOptions = useFinancialYears();

  // Transform statesData to get states and allSites
  const states = statesData
    ? statesData.map((item) => ({ state: item.state }))
    : [];
  const allSites = statesData ? statesData.flatMap((item) => item.stores) : [];
  const companies = companiesData ? companiesData : [];

  // Filter sites based on selected states
  const filteredSites =
    filters.states.length > 0
      ? statesData
        .filter((state) =>
          filters.states.some(
            (selectedState) => selectedState.state === state.state
          )
        )
        .flatMap((state) => state.stores)
      : allSites;

  const { reportData, title, loading, error, refetch } = useReportData(
    props?.request_name,
    appliedFilters,
    buildQueryString
  );

  useEffect(() => {
    if (error) {
      setMessageState("error");
      setMessage(error.response?.data?.message || "An error occurred");
      setOpen(true);
    }
  }, [error]);

  // Define columns for each table type - Updated for new API response
  const stateColumns = [
    { id: "state_name", name: "State" },
    { id: "zont_mile_sales", name: "Zont Mile Sales", formatCurrency: true },
    { id: "zont_mile_transactions", name: "Zont Mile Transactions" },
    { id: "comparison_sales", name: "Comparison Sales", formatCurrency: true },
    { id: "comparison_transactions", name: "Comparison Transactions" },
  ];

  const storeColumns = [
    { id: "store_name", name: "Store" },
    { id: "zont_mile_sales", name: "Zont Mile Sales", formatCurrency: true },
    { id: "zont_mile_transactions", name: "Zont Mile Transactions" },
    { id: "comparison_sales", name: "Comparison Sales", formatCurrency: true },
    { id: "comparison_transactions", name: "Comparison Transactions" },
  ];

  const companyColumns = [
    { id: "company_name", name: "Company" },
    { id: "zont_mile_sales", name: "Zont Mile Sales", formatCurrency: true },
    { id: "zont_mile_transactions", name: "Zont Mile Transactions" },
    { id: "comparison_sales", name: "Comparison Sales", formatCurrency: true },
    { id: "comparison_transactions", name: "Comparison Transactions" },
  ];

  // Filter functions
  const updateFilter = (name, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      // Handle financial year and date range exclusivity
      if (name === "financialYear" && value) {
        newFilters.zontMileRange = [null, null];
        newFilters.comparisonRange = [null, null];
      } else if (
        (name === "zontMileRange" || name === "comparisonRange") &&
        (value[0] || value[1])
      ) {
        newFilters.financialYear = null;
      }
      return newFilters;
    });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setHasApplied(false);
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    setHasApplied(true);
  };

  // Helper function to check if a date range is active
  const isDateRangeActive = (dateRange) => {
    return Array.isArray(dateRange) && (dateRange[0] !== null || dateRange[1] !== null);
  };

  // Updated hasActiveFilters condition
  const hasActiveFilters = hasApplied && (
    appliedFilters.sites.length > 0 ||
    appliedFilters.states.length > 0 ||
    appliedFilters.companies.length > 0 ||
    appliedFilters.financialYear !== null ||
    isDateRangeActive(appliedFilters.zontMileRange) ||
    isDateRangeActive(appliedFilters.comparisonRange)
  );

  const isRemoving = useRef(false);
  const removeFilter = (filterName) => {
    isRemoving.current = true;
    if (filterName === "zontMileRange") {
      updateFilter("zontMileRange", [null, null]);
    } else if (filterName === "comparisonRange") {
      updateFilter("comparisonRange", [null, null]);
    } else {
      updateFilter(
        filterName,
        filterName === "sites"
          ? []
          : filterName === "states"
            ? []
            : filterName === "companies"
              ? []
              : filterName === "financialYear"
                ? null
                : undefined
      );
    }
  };

  useEffect(() => {
    if (isRemoving.current) {
      applyFilters();
      isRemoving.current = false;
    }
  }, [filters, applyFilters]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
    setTokenOpen(false);
  };

  // Function to transform data for graph - Updated for new API response
  function transformZontMileDataForGraph() {
    const allData = [];

    // Add state data with type prefix
    if (reportData?.by_state && reportData.by_state.length > 0) {
      reportData.by_state.forEach(item => {
        if (item.state_name !== "SUB TOTAL") { // Skip subtotal row
          allData.push({
            ...item,
            category_name: `State: ${item.state_name}`,
            category_type: 'state'
          });
        }
      });
    }

    // Add store data with type prefix
    if (reportData?.by_store && reportData.by_store.length > 0) {
      reportData.by_store.forEach(item => {
        if (item.store_name !== "SUB TOTAL") { // Skip subtotal row
          allData.push({
            ...item,
            category_name: `Store: ${item.store_name}`,
            category_type: 'store'
          });
        }
      });
    }

    // Add company data with type prefix
    if (reportData?.by_company && reportData.by_company.length > 0) {
      reportData.by_company.forEach(item => {
        if (item.company_name !== "SUB TOTAL") { // Skip subtotal row
          allData.push({
            ...item,
            category_name: `Company: ${item.company_name}`,
            category_type: 'company'
          });
        }
      });
    }

    if (allData.length === 0) return { categories: [], series: [] };

    // Extract categories
    const categories = allData.map(item => item.category_name);

    // Define keys to exclude from series
    const excludeKeys = [
      "state_name",
      "store_name",
      "company_name",
      "category_name",
      "category_type"
    ];

    // Get all numeric fields except excluded keys
    const numericKeys = Object.keys(allData[0]).filter(
      key => !excludeKeys.includes(key)
    );

    // Build Highcharts series
    const series = numericKeys.map(key => ({
      name: key
        .replace(/_/g, " ") // Replace underscores with spaces
        .replace(/\b\w/g, c => c.toUpperCase()), // Capitalize words
      data: allData.map(item => {
        // Convert string to number if necessary
        const value = item[key];
        return typeof value === 'string' ? parseFloat(value) : value;
      }),
    }));

    return { categories, series };
  }

  const { categories, series } = transformZontMileDataForGraph();

  const getScheduleData = async () => {
    try {
      const response = await httpclient.get(
        `${props?.request_name}/report-schedules`
      );
      if (response.status === 200) {
        const data = response.data.data;
        setScheduleId(data.id);

        // Format time from "09:00:00" to "09:00"
        const formattedTime = data.send_time
          ? data.send_time.substring(0, 5)
          : "09:00";

        setScheduleData({
          time: formattedTime,
          frequency: data.frequency,
          recipients: data.recipients || "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch schedule data:", error);
      // Set default values on error
      setScheduleData({
        time: "09:00",
        frequency: "daily",
        recipients: "",
      });
    }
  };

  const handleScheduleSubmit = async () => {
    try {
      const user = localStorage.getItem("user");
      const userData = JSON.parse(user);
      const schedulePayload = {
        frequency: schedule.frequency,
        send_time: schedule.time,
        recipients: schedule.recipients,
        report_name: "double-zont-sales",
        last_updated_by: userData?.user_name,
      };

      console.log("Scheduling report:", schedulePayload);
      const res = await httpclient.put(
        `request-response?requestName=lightspeed/advanced-report/report-schedules/${scheduleId}`,
        schedulePayload
      );
      if (res.status === 200) {
        setMessageState("success");
        setMessage(
          `Report scheduled to be sent every ${schedule.frequency} days at ${schedule.time}`
        );
        setOpen(true);
        getScheduleData();
      }
      setScheduleOpen(false);
    } catch (error) {
      setMessageState("error");
      setMessage("Failed to schedule report. Please try again.");
      setOpen(true);
    }
  };

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Header>
            <h1>Zont Mile Sales Comparison Report</h1>
          </Header>
        </Grid>
        <Grid
          item
          md={4}
          xs={12}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
        >
          <Button
            color="primary"
            variant="contained"
            onClick={() => setScheduleOpen(!scheduleOpen)}
            sx={{ mr: 1 }}
          >
            Schedule Report Email{" "}
            <Schedule style={{ marginLeft: "5px" }} fontSize="small" />
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            Filter <FilterList style={{ marginLeft: "5px" }} fontSize="small" />
          </Button>
        </Grid>

        {/* Custom Filter UI */}
        <CustomReportFilter
          open={filterOpen}
          filters={filters}
          states={states}
          sites={filteredSites}
          companies={companies}
          financialYearOptions={financialYearOptions}
          onFilterChange={updateFilter}
          onApply={applyFilters}
          onReset={resetFilters}
          includeDates={includeDates}
          loading={initialDataLoading}
        />

        <ScheduleReport
          open={scheduleOpen}
          schedule={schedule}
          onScheduleChange={updateSchedule}
          onSubmit={handleScheduleSubmit}
        />

        {/* Custom Active Filters Component */}
        {hasActiveFilters && (
          <ActiveFiltersComponent
            filters={appliedFilters}
            onRemoveFilter={removeFilter}
            showTime={false}
          />
        )}

        <Grid item xs={12} mb={4}>
          <VisualizationGraph
            title={`Zont Mile Sales Comparison Report ${title}`}
            subtitle={"Sales & Transactions"}
            categories={categories}
            series={series}
            loading={loading}
          />
        </Grid>

        {/* State Table - Only show if data exists */}
        {reportData?.by_state && reportData.by_state.length > 0 && (
          <Grid item xs={12} mb={4}>
            <Typography variant="h6" gutterBottom>
              Zont Mile Sales Comparison by State
            </Typography>
            <ReportTable
              columns={stateColumns}
              rows={reportData.by_state}
              loading={loading}
              reportView="state"
            />
          </Grid>
        )}

        {/* Store Table - Only show if data exists */}
        {reportData?.by_store && reportData.by_store.length > 0 && (
          <Grid item xs={12} mb={4}>
            <Typography variant="h6" gutterBottom>
              Zont Mile Sales Comparison by Store
            </Typography>
            <ReportTable
              columns={storeColumns}
              rows={reportData.by_store}
              loading={loading}
              reportView="store"
            />
          </Grid>
        )}

        {/* Company Table - Only show if data exists */}
        {reportData?.by_company && reportData.by_company.length > 0 && (
          <Grid item xs={12} mb={4}>
            <Typography variant="h6" gutterBottom>
              Zont Mile Sales Comparison by Company
            </Typography>
            <ReportTable
              columns={companyColumns}
              rows={reportData.by_company}
              loading={loading}
              reportView="company"
            />
          </Grid>
        )}

        {/* Show message if no data is available */}
        {!loading &&
          (!reportData?.by_state || reportData.by_state.length === 0) &&
          (!reportData?.by_store || reportData.by_store.length === 0) &&
          (!reportData?.by_company || reportData.by_company.length === 0) && (
            <Grid item xs={12}>
              <Typography align="center" variant="body1">
                No data available for the selected filters.
              </Typography>
            </Grid>
          )}

        <Footer overlay={overlay || props.overlayNew} />
      </Grid>

      <Snackbar
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open || tokenOpen}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={messageState || tokenMessageState}
          sx={{ width: "100%" }}
        >
          {message || tokenMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DoubleZontMileSales;